import {getSingleInputByNameV3} from "../../../helpers/forms/formInputsHelpers";
import Axios from "axios";
import {loading, stopLoading} from "../../../helpers/loading";
import {displayLocalValidationError} from "../../../helpers/forms/validationErrors/utils";
import Swal from "sweetalert2";

class TotalPreviewComponent {

    /** @var {HTMLInputElement} */
    discountCodeInput;

    /** @var {boolean} */
    withTOSWarning;

    /** @var {number|null} */
    debounceTimeout = null;

    /** @var {Array} */
    pendingPromises = [];

    /** @var {boolean} */
    staleTotals = false;

    discountCodeChangeCallbacks = [];

    /**
     * @param {HTMLElement} container
     * @param {GlobalState} state
     */
    constructor(container, state) {
        this.container = container
        this.state = state;

        this.withTOSWarning = false;

        this.getTotalsPreviewHtmlRoute = getSingleInputByNameV3(document.body, 'get_totals_preview_html_route');
    }

    async updateTotalsFromServer() {
        return new Promise((resolve, reject) => {
            // Clear existing timeout
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
            }

            // Add this promise to the pending list
            this.pendingPromises.push({resolve, reject});

            // Set new timeout
            // noinspection JSValidateTypes
            this.debounceTimeout = setTimeout(async () => {
                // Execute update totals with current awaiting callbacks
                const batchPromises = this.pendingPromises;
                // Clear array so any new calls gets processed by future debounced calls
                this.pendingPromises = [];

                if (this.isHiddenStagePreview()) {
                    this.staleTotals = true;
                    batchPromises.forEach(({resolve}) => resolve());
                    this.debounceTimeout = null;
                    return;
                }

                try {
                    // Execute the actual update logic
                    await this._executeUpdateTotalsFromServer();

                    // Resolve all pending promises
                    batchPromises.forEach(({resolve}) => resolve());
                } catch (error) {
                    // Reject all pending promises
                    batchPromises.forEach(({reject}) => reject(error));
                }

                this.debounceTimeout = null;
            }, 150);
        });
    }

    async _executeUpdateTotalsFromServer() {
        // Get new total HTML
        try {
            this.staleTotals = false;

            const orderData = this.state.getOrderDataForServer();

            if (this.withTOSWarning) {
                orderData.withTOSWarning = true;
            }

            const result = await Axios.post(
                this.getTotalsPreviewHtmlRoute,
                orderData
            );

            // Append to container
            this.container.innerHTML = result.data.body;

            // Initialize components
            this.initializeComponents();
        } catch (error) {
            if (error.response && error.response.data && error.response.data.errors) {
                const validationErrors = error.response.data.errors

                // If a wrong discount was passed
                if (validationErrors.invalid_discount_code) {
                    const rejectedDiscountCode = this.state.discountCode;

                    // Clear discount from state
                    this.state.setDiscountCode('');

                    // Then get a fresh order summary from the server
                    void this.updateTotalsFromServer()
                        .then(() => {
                            void this.trySaveInvalidDiscountAsTrackingCode(rejectedDiscountCode);
                        })
                        .catch(() => {
                            void this.trySaveInvalidDiscountAsTrackingCode(rejectedDiscountCode);
                        });

                    // And show error to user
                    displayLocalValidationError(
                        document.body,
                        'DUMMY',
                        validationErrors.invalid_discount_code
                    )
                }

                // If a discount no longer applies
                if (validationErrors.discount_constraints_not_met) {
                    // Clear discount from state
                    this.state.setDiscountCode('');

                    // Then get a fresh order summary from the server
                    this.updateTotalsFromServer().then();

                    // TODO:    Show better message to user 'no longer applies' vs 'does not apply' for
                    //          first time coupon use
                    // And show error to user
                    displayLocalValidationError(
                        document.body,
                        'DUMMY',
                        'Discount does not apply to this order'
                    )
                }

                // If a discount is superseded by system discount
                if (validationErrors.system_discount_is_better_than_code_discount) {
                    // Clear discount from state
                    this.state.setDiscountCode('');

                    // Then get a fresh order summary from the server
                    this.updateTotalsFromServer().then();

                    // TODO:    Show better message to user 'no longer applies' vs 'does not apply' for
                    //          first time coupon use
                    // And show error to user
                    displayLocalValidationError(
                        document.body,
                        'DUMMY',
                        'A better discount has been applied automatically to your order'
                    )
                }

                if (validationErrors.not_enough_seats_available) {
                    // Requested seats exceed available seats
                    displayLocalValidationError(
                        document.body,
                        'DUMMY',
                        validationErrors.not_enough_seats_available[0]
                    )

                    this.recoverFromCapacityConflict();
                }
            }
        }
    }

    recoverFromCapacityConflict() {
        this.state.setGeneralAdmissionTicketCounts([]);
        this.state.setReserveTicketCounts([]);
        this.state.recoveringFromCapacityConflict = true;

        const ticketTypeStage = document.querySelector('.stage.seat-type-select');
        if (ticketTypeStage) {
            ticketTypeStage.innerHTML = '';
        }

        this.state.stageManager.clearStageInstance('seat-type-select');
        this.state.stageManager.goToStage('seat-type-select');
    }

    isHiddenStagePreview() {
        if (this.state.isExpressMode()) {
            return false;
        }

        const stage = this.container.closest('.stage');

        return stage && stage.classList.contains('d-none');
    }

    hasStaleTotals() {
        return this.staleTotals;
    }

    /**
     * Initializes fresh HTML after it has been retrieved from the server
     */
    initializeComponents() {
        this.getDiscountRoute = getSingleInputByNameV3(this.container, 'get_discount_route');
        this.eventId = getSingleInputByNameV3(this.container, 'event_id');

        // Init discount code input
        this.discountCodeInput = this.container.querySelector('input[name="discount_code"]');

        this.discountCodeInput.addEventListener('keyup', e => {
            if (e.key === 'Enter') {
                this.handleDiscountCodeUseRequest();
            }
        })

        // Setup event handlers
        this.container.querySelector('.use-discount-code-button')
            .addEventListener('click', () => {
                this.handleDiscountCodeUseRequest()
            })

        this.initializeManualTrackingCodeInput();
    }

    initializeManualTrackingCodeInput() {
        this.manualTrackingCodeButton = this.container.querySelector('.manual-tracking-code-button');

        if (!this.manualTrackingCodeButton) {
            return;
        }

        this.manualTrackingCodeButton.addEventListener('click', () => {
            this.handleManualTrackingCodeRequest();
        });
    }

    handleManualTrackingCodeRequest() {
        const publicTrackingCodeName = this.getPublicTrackingCodeName();

        Swal.fire({
            title: `Input ${publicTrackingCodeName}`,
            input: 'text',
            inputValue: this.state.adTrackingKeyword || '',
            inputAttributes: {
                autocapitalize: 'off',
                autocomplete: 'off',
            },
            showCancelButton: true,
            confirmButtonText: 'Save',
            showLoaderOnConfirm: true,
            preConfirm: async (trackingCode) => {
                const normalizedCode = String(trackingCode || '').trim().toLowerCase();

                if (normalizedCode.includes('/')) {
                    Swal.showValidationMessage(`${publicTrackingCodeName} cannot contain slashes`);
                    return false;
                }

                try {
                    loading()
                    const response = await Axios.get(this.getManualTrackingCodeSaveRoute(normalizedCode));

                    stopLoading();
                    return {
                        code: normalizedCode,
                        message: response.data.message || 'Code saved',
                    };
                } catch (error) {
                    stopLoading();
                    Swal.showValidationMessage(this.getManualTrackingCodeErrorMessage(error, publicTrackingCodeName));
                    return false;
                }
            },
            allowOutsideClick: () => !Swal.isLoading(),
        }).then(async result => {
            if (!result.isConfirmed || !result.value) {
                return;
            }

            this.state.setManualTrackingCode(result.value.code);

            loading();

            await this.updateTotalsFromServer()
                .finally(() => {
                    stopLoading();
                });

            void Swal.fire({
                icon: 'success',
                title: result.value.message,
            });
        });
    }

    getManualTrackingCodeSaveRoute(trackingCode) {
        if (!trackingCode) {
            return this.manualTrackingCodeButton.dataset.clearRoute;
        }

        return this.manualTrackingCodeButton.dataset.saveRoute.replace(
            '__TRACKING_CODE__',
            encodeURIComponent(trackingCode)
        );
    }

    getRegisteredOnlyManualTrackingCodeSaveRoute(trackingCode) {
        const route = this.getManualTrackingCodeSaveRoute(trackingCode);
        const separator = route.includes('?') ? '&' : '?';

        return `${route}${separator}registered_only=1`;
    }

    async trySaveInvalidDiscountAsTrackingCode(discountCode) {
        if (!this.manualTrackingCodeButton || this.state.adTrackingKeyword) {
            return;
        }

        const normalizedCode = String(discountCode || '').trim().toLowerCase();

        if (!normalizedCode || normalizedCode.includes('/')) {
            return;
        }

        try {
            await Axios.get(this.getRegisteredOnlyManualTrackingCodeSaveRoute(normalizedCode));
            this.state.setManualTrackingCode(normalizedCode);
        } catch (error) {
            // Silent fallback: invalid discount UX should remain unchanged.
        }
    }

    getPublicTrackingCodeName() {
        if (!this.manualTrackingCodeButton) {
            return 'tracking code';
        }

        const publicTrackingCodeName = String(this.manualTrackingCodeButton.dataset.publicTrackingCodeName || '').trim();

        if (!publicTrackingCodeName) {
            return 'tracking code';
        }

        return publicTrackingCodeName;
    }

    getManualTrackingCodeErrorMessage(error, publicTrackingCodeName = 'tracking code') {
        if (error.response && error.response.data && error.response.data.errors) {
            const errors = error.response.data.errors;

            if (errors.tracking_code) {
                return Array.isArray(errors.tracking_code) ? errors.tracking_code[0] : errors.tracking_code;
            }
        }

        return `${publicTrackingCodeName} could not be saved`;
    }

    handleDiscountCodeUseRequest() {
        const discountCode = this.discountCodeInput.value.trim();

        if (discountCode) {
            loading();
            this.state.setDiscountCode(discountCode)

            // Refresh totals
            this.updateTotalsFromServer()
                .then(() => {
                    stopLoading();
                    // Notify callbacks
                    this.discountCodeChangeCallbacks.forEach(callback => {
                        callback();
                    })
                })

        } else {
            // Clear discount
            this.state.setDiscountCode('');
            // Refresh totals
            this.updateTotalsFromServer().then(() => {
                // Notify callbacks
                this.discountCodeChangeCallbacks.forEach(callback => {
                    callback();
                })
            });
        }
    }

    addDiscountCodeChangeListener(callback) {
        this.discountCodeChangeCallbacks.push(callback);
    }

    getOrderTotal() {
        const totalFromServer = parseFloat(getSingleInputByNameV3(this.container, 'order_total'));

        if (isNaN(totalFromServer)) {
            return 0;
        }

        return totalFromServer
    }
}

export {TotalPreviewComponent}
